import * as THREE from 'three';

export const DAMAGE_DECAL_ATLAS_DEFINE = 'USE_DAMAGE_DECAL_ATLAS';
export const DAMAGE_ATLAS_URL = '/textures/damage-atlas.png';
export const DAMAGE_ATLAS_BRICK_URL = '/textures/damage-atlas-brick.png';
export const DAMAGE_ATLAS_STONE_URL = '/textures/damage-atlas-stone.png';
export const DAMAGE_ATLAS_WOOD_URL = '/textures/damage-atlas-wood.png';
export const DAMAGE_DECAL_STAMP_CAP = 48;

/** Atlas layout: 4 cols × 4 rows of 512px on a 2048×2048 sheet. */
export const DECAL_ATLAS_COLS = 4;
export const DECAL_ATLAS_ROWS = 4;

function makeStampVec4s(): THREE.Vector4[] {
  return Array.from({ length: DAMAGE_DECAL_STAMP_CAP }, () => new THREE.Vector4());
}

function makeStampFloats(): number[] {
  return Array.from({ length: DAMAGE_DECAL_STAMP_CAP }, () => 0);
}

export const damageDecalUniforms = {
  uDecalAtlas: { value: null as THREE.Texture | null },
  uDecalAtlasBrick: { value: null as THREE.Texture | null },
  uDecalAtlasStone: { value: null as THREE.Texture | null },
  uDecalAtlasWood: { value: null as THREE.Texture | null },
  uDecalAtlasReady: { value: 0 },
  uDecalStampCount: { value: 0 },
  uDecalStampA: { value: makeStampVec4s() },
  uDecalStampB: { value: makeStampVec4s() },
  /** Per-stamp influence radius (fact.radius, energy-scaled at pack time). */
  uDecalStampR: { value: makeStampFloats() },
  /** Wall origin in world space — restWorld = origin + surfaceRestPosition. */
  uDecalWallOrigin: { value: new THREE.Vector3(0, 0, 0) },
  /** 1 during atlas bake-down so rest-fade does not kill stamps. */
  uBakeDownDecals: { value: 0 },
};

let loadPromise: Promise<boolean> | null = null;
let atlasTexture: THREE.Texture | null = null;
let brickAtlasTexture: THREE.Texture | null = null;
let stoneAtlasTexture: THREE.Texture | null = null;
let woodAtlasTexture: THREE.Texture | null = null;

export function isDecalAtlasReady(): boolean {
  return damageDecalUniforms.uDecalAtlasReady.value > 0.5;
}

/** Loaded atlas textures (null until `ensureDecalAtlasLoaded` resolves). */
export function getDecalAtlasTextures(): {
  concrete: THREE.Texture | null;
  brick: THREE.Texture | null;
  stone: THREE.Texture | null;
  wood: THREE.Texture | null;
} {
  return {
    concrete: atlasTexture,
    brick: brickAtlasTexture,
    stone: stoneAtlasTexture,
    wood: woodAtlasTexture,
  };
}

export function writeDecalStampUniforms(next: {
  count: number;
  a: THREE.Vector4[];
  b: THREE.Vector4[];
  r?: number[];
}): void {
  damageDecalUniforms.uDecalStampCount.value = next.count;
  const a = damageDecalUniforms.uDecalStampA.value;
  const b = damageDecalUniforms.uDecalStampB.value;
  const r = damageDecalUniforms.uDecalStampR.value;
  for (let i = 0; i < DAMAGE_DECAL_STAMP_CAP; i += 1) {
    a[i].copy(next.a[i]);
    b[i].copy(next.b[i]);
    r[i] = next.r?.[i] ?? 0;
  }
}

export function setDecalWallOrigin(origin: { x: number; y: number; z: number }): void {
  damageDecalUniforms.uDecalWallOrigin.value.set(origin.x, origin.y, origin.z);
}

export function clearDecalStampUniforms(): void {
  damageDecalUniforms.uDecalStampCount.value = 0;
  const a = damageDecalUniforms.uDecalStampA.value;
  const b = damageDecalUniforms.uDecalStampB.value;
  const r = damageDecalUniforms.uDecalStampR.value;
  for (let i = 0; i < DAMAGE_DECAL_STAMP_CAP; i += 1) {
    a[i].set(0, 0, 0, 0);
    b[i].set(0, 0, 0, 0);
    r[i] = 0;
  }
}

function markRuntimeReady(ready: boolean): void {
  if (typeof window === 'undefined') return;
  const exposed = window as Window & {
    __demolitionRuntime?: {
      decalAtlasReady?: boolean;
      decalAtlasBrickReady?: boolean;
      decalAtlasStoneReady?: boolean;
      decalAtlasWoodReady?: boolean;
    };
    __decalAtlasReady?: boolean;
  };
  exposed.__decalAtlasReady = ready;
  if (exposed.__demolitionRuntime) {
    exposed.__demolitionRuntime.decalAtlasReady = ready;
    exposed.__demolitionRuntime.decalAtlasBrickReady = ready;
    exposed.__demolitionRuntime.decalAtlasStoneReady = ready;
    exposed.__demolitionRuntime.decalAtlasWoodReady = ready;
  }
}

function configureAtlasTexture(texture: THREE.Texture): THREE.Texture {
  // Keep Three's default flipY=true so PNG top (row 0) lands at v=1.
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  texture.anisotropy = 4;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function loadAtlasTexture(url: string): Promise<THREE.Texture | null> {
  return new Promise((resolve) => {
    const loader = new THREE.TextureLoader();
    loader.load(
      url,
      (texture) => resolve(configureAtlasTexture(texture)),
      undefined,
      () => resolve(null),
    );
  });
}

export function ensureDecalAtlasLoaded(): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (loadPromise) return loadPromise;
  loadPromise = Promise.all([
    loadAtlasTexture(DAMAGE_ATLAS_URL),
    loadAtlasTexture(DAMAGE_ATLAS_BRICK_URL),
    loadAtlasTexture(DAMAGE_ATLAS_STONE_URL),
    loadAtlasTexture(DAMAGE_ATLAS_WOOD_URL),
  ]).then(([concrete, brick, stone, wood]) => {
    if (concrete) {
      atlasTexture = concrete;
      damageDecalUniforms.uDecalAtlas.value = concrete;
    }
    if (brick) {
      brickAtlasTexture = brick;
      damageDecalUniforms.uDecalAtlasBrick.value = brick;
    }
    if (stone) {
      stoneAtlasTexture = stone;
      damageDecalUniforms.uDecalAtlasStone.value = stone;
    }
    if (wood) {
      woodAtlasTexture = wood;
      damageDecalUniforms.uDecalAtlasWood.value = wood;
    }
    // Ready when concrete loads (existing probes); other sheets are co-loaded.
    const ready = concrete !== null;
    damageDecalUniforms.uDecalAtlasReady.value = ready ? 1 : 0;
    markRuntimeReady(ready);
    return ready;
  });
  return loadPromise;
}

export function assignDamageDecalUniforms(shader: {
  uniforms: Record<string, { value: unknown }>;
}): void {
  Object.assign(shader.uniforms, damageDecalUniforms);
}

export function applyDamageDecalAtlasDefine(material: THREE.Material): void {
  const mat = material as THREE.MeshStandardMaterial;
  if (!mat.defines) mat.defines = {};
  mat.defines[DAMAGE_DECAL_ATLAS_DEFINE] = '';
}

/** Test-only: drop module state so files that share a worker stay unbound. */
export function resetDamageDecalAtlasForTests(): void {
  damageDecalUniforms.uDecalAtlas.value = null;
  damageDecalUniforms.uDecalAtlasBrick.value = null;
  damageDecalUniforms.uDecalAtlasStone.value = null;
  damageDecalUniforms.uDecalAtlasWood.value = null;
  damageDecalUniforms.uDecalAtlasReady.value = 0;
  damageDecalUniforms.uDecalWallOrigin.value.set(0, 0, 0);
  clearDecalStampUniforms();
  atlasTexture?.dispose();
  brickAtlasTexture?.dispose();
  stoneAtlasTexture?.dispose();
  woodAtlasTexture?.dispose();
  atlasTexture = null;
  brickAtlasTexture = null;
  stoneAtlasTexture = null;
  woodAtlasTexture = null;
  loadPromise = null;
}

/**
 * Shared atlas-decal fragment helpers. Requires DAMAGE_FIELD_GLSL first
 * (gDamage, uDamageFieldTime). Exterior materials define USE_DAMAGE_DECAL_ATLAS.
 *
 * Concrete atlas: 2048×2048, 4×4 tiles. Rows: heavy, medium, light, gouge.
 * Brick atlas: same layout. Rows: partial-face, cluster, skid, mortar-dust.
 * Stone atlas: same layout. Rows: heavy edge-spall, medium stellate, light pocks, gouge.
 * Wood atlas: same layout. Rows: heavy puncture, medium split, light scrape, gouge.
 * Wood grazing is shader-anisotropic (mix stamp +X toward rest-space grain) —
 * a second baked orientation is not used.
 * Shared stamp ring / rest-fade / rim helper; material-specific sample entry points.
 */
export const DAMAGE_DECAL_ATLAS_GLSL = /* glsl */ `
#ifdef USE_DAMAGE_DECAL_ATLAS
#ifdef USE_DAMAGE_FIELD
#ifndef DAMAGE_DECAL_STAMP_CAP
#define DAMAGE_DECAL_STAMP_CAP 48
#endif
uniform sampler2D uDecalAtlas;
uniform sampler2D uDecalAtlasBrick;
uniform sampler2D uDecalAtlasStone;
uniform sampler2D uDecalAtlasWood;
uniform float uDecalAtlasReady;
uniform float uDecalStampCount;
uniform vec4 uDecalStampA[DAMAGE_DECAL_STAMP_CAP];
uniform vec4 uDecalStampB[DAMAGE_DECAL_STAMP_CAP];
uniform float uDecalStampR[DAMAGE_DECAL_STAMP_CAP];
uniform vec3 uDecalWallOrigin;

float gDecalSpall = 0.0;
float gDecalCrack = 0.0;
float gDecalCrackCore = 0.0;
float gDecalDepth = 0.0;
float gDecalAo = 0.0;
float gDecalFresh = 1.0;
// Thin lit band just outside the G-mask isoline (chipped-edge catch light).
float gDecalRim = 0.0;
// 1 at rest; fades to 0 by ~3 cm of cell displacement (authored network only).
float gDecalRestFade = 1.0;
// Brick-only extras (stair mortar crack + corner chip bias). Zero on concrete path.
float gBrickStairCrack = 0.0;
float gBrickStairHair = 0.0;
float gBrickCornerChip = 0.0;
float gBrickSkid = 0.0;
float gStoneJointCrack = 0.0;
float gStoneJointHair = 0.0;
float gStoneCornerChip = 0.0;
float gStoneSkid = 0.0;
float gWoodFurrow = 0.0;
varying float vDecalRestFade;
#ifndef U_BAKE_DOWN_DECALS
#define U_BAKE_DOWN_DECALS
uniform float uBakeDownDecals;
#endif

float decalEffectiveRestFade() {
  return uBakeDownDecals > 0.5 ? 1.0 : vDecalRestFade;
}

vec2 decalFaceCoords(vec3 delta, vec3 restNormal) {
  vec3 an = abs(restNormal);
  if (an.z >= an.x && an.z >= an.y) return delta.xy;
  if (an.x >= an.y) return delta.yz;
  return delta.xz;
}

vec3 decalFaceNormal(vec3 restNormal) {
  vec3 an = abs(restNormal);
  if (an.z >= an.x && an.z >= an.y) return vec3(0.0, 0.0, sign(restNormal.z));
  if (an.x >= an.y) return vec3(sign(restNormal.x), 0.0, 0.0);
  return vec3(0.0, sign(restNormal.y), 0.0);
}

float decalHash11(float n) {
  return fract(sin(n * 127.1) * 43758.5453);
}

// Row: 0 heavy, 1 medium, 2 light, 3 gouge. Grazing overrides to gouge.
// Threshold 0.28 matches live contact grazing (oblique hits rarely exceed ~0.4).
float decalRowFromEnergy(float energy, float grazing) {
  if (grazing > 0.28) return 3.0;
  if (energy < 0.28) return 2.0;
  if (energy < 0.58) return 1.0;
  return 0.0;
}

// flipY=true: PNG top (row 0) → high v. Physical row → atlas v base.
// 4 rows share the sheet equally: each tile is 0.25 of atlas UV.
vec2 decalAtlasUv(float variant, float row, vec2 uv) {
  float atlasRow = 3.0 - row;
  return vec2((variant + uv.x) * 0.25, (atlasRow + uv.y) * 0.25);
}

// Shared rim + crack + spall accumulate (concrete freeform + brick atlas samples).
void decalAccumulateSample(
  vec4 samp,
  float fresh,
  float opacity,
  vec2 uv
) {
  float spall = samp.g * fresh * opacity;
  gDecalSpall = 1.0 - (1.0 - gDecalSpall) * (1.0 - spall);

  float depth = samp.b * samp.g * fresh * opacity;
  gDecalDepth = max(gDecalDepth, depth);

  // True variable-radius: 0.5 is the tapered outline; <0.5 is inside.
  // Continuity comes from the signed field; AA uses fwidth of the isoline
  // (≥~1.5 px). Never a fixed-width G/R threshold — that beads at ~256 px.
  float signed = samp.r - 0.5;
  float uvPx = max(fwidth(uv.x), fwidth(uv.y));
  float aa = max(fwidth(signed), max(uvPx * 1.5, 1.5e-4));
  float inside = 1.0 - smoothstep(-aa, aa, signed);
  float crack = inside * fresh * opacity;
  gDecalCrack = max(gDecalCrack, crack);

  // Lit rim: fwidth-widthed band just outside the G isoline. A fixed G-unit
  // floor (old 0.028) became sub-pixel at close zoom and beaded the edge.
  float gMask = samp.g;
  float iso = 0.16;
  float gSigned = gMask - iso;
  float fwG = max(fwidth(gSigned), max(uvPx * 1.5, 1.5e-4));
  float outside = -gSigned;
  float rim = smoothstep(-fwG * 0.25, fwG * 0.55, outside)
    * (1.0 - smoothstep(fwG * 0.90, fwG * 2.4, outside));
  gDecalRim = max(gDecalRim, rim * fresh * opacity);

  // Centerline grade: more negative signed → deeper in stroke.
  float core = clamp(-signed / 0.10, 0.0, 1.0) * inside * fresh * opacity;
  gDecalCrackCore = max(gDecalCrackCore, core);

  // AO recess band just outside the outline (signed > 0) — recessed read, not paint.
  float ao = (1.0 - smoothstep(aa * 0.5, aa + max(uvPx * 5.0, 0.04), max(signed, 0.0)))
    * (1.0 - inside * 0.25)
    * fresh
    * opacity;
  gDecalAo = max(gDecalAo, ao);
}

void sampleDamageDecals(vec3 restPos, vec3 restNormal) {
  gDecalSpall = 0.0;
  gDecalCrack = 0.0;
  gDecalCrackCore = 0.0;
  gDecalDepth = 0.0;
  gDecalAo = 0.0;
  gDecalRim = 0.0;
  gDecalFresh = 1.0;
  gDecalRestFade = decalEffectiveRestFade();
  gBrickStairCrack = 0.0;
  gBrickStairHair = 0.0;
  gBrickCornerChip = 0.0;
  gBrickSkid = 0.0;
  gStoneJointCrack = 0.0;
  gStoneJointHair = 0.0;
  gStoneCornerChip = 0.0;
  gStoneSkid = 0.0;
  gWoodFurrow = 0.0;
  if (uDecalAtlasReady < 0.5) return;
  // Displaced debris: drop authored network (bruise field may stay via gDamage).
  if (decalEffectiveRestFade() < 0.02) return;

  int n = int(uDecalStampCount + 0.5);
  if (n <= 0) return;
  vec3 faceN = decalFaceNormal(restNormal);

  if (gDamage < 0.001) {
    bool nearStamp = false;
    for (int j = 0; j < DAMAGE_DECAL_STAMP_CAP; j++) {
      if (j >= n) break;
      float e = clamp(uDecalStampB[j].x, 0.0, 1.0);
      float reach = mix(0.42, 1.62, e) * 0.78;
      vec3 dlt = restPos - uDecalStampA[j].xyz;
      vec2 pl = decalFaceCoords(dlt, restNormal);
      if (dot(pl, pl) < reach * reach && abs(dot(dlt, faceN)) < 0.14
          && abs(dot(normalize(restNormal), faceN)) >= 0.62) {
        nearStamp = true;
        break;
      }
    }
    if (!nearStamp) return;
  }

  float restFade = decalEffectiveRestFade();

  for (int i = 0; i < DAMAGE_DECAL_STAMP_CAP; i++) {
    if (i >= n) break;
    vec4 a = uDecalStampA[i];
    vec4 b = uDecalStampB[i];
    vec3 stampPos = a.xyz;
    float tangentAngle = a.w;
    float energy = clamp(b.x, 0.0, 1.0);
    float seed = b.y;
    float birth = b.z;
    float variant = floor(b.w + 1e-4);
    float grazing = clamp(fract(b.w) / 0.99, 0.0, 1.0);

    vec3 delta = restPos - stampPos;
    if (abs(dot(delta, faceN)) > 0.14) continue;
    if (abs(dot(normalize(restNormal), faceN)) < 0.62) continue;

    // Scale jitter ±30% from seed (on top of energy scaling).
    float scaleJit = mix(0.70, 1.30, decalHash11(seed * 17.13 + 0.7));
    float worldSize = mix(0.42, 1.62, energy) * scaleJit;
    vec2 planar = decalFaceCoords(delta, restNormal);
    if (dot(planar, planar) > worldSize * worldSize * 0.54) continue;

    // Small seed dither on rotation + optional mirror flip (free 2×).
    float rot = tangentAngle + (seed - 0.5) * 0.38;
    bool mirror = decalHash11(seed * 3.1 + 1.7) > 0.5;
    float cs = cos(-rot);
    float sn = sin(-rot);
    vec2 local = vec2(cs * planar.x - sn * planar.y, sn * planar.x + cs * planar.y);
    if (mirror) local.x = -local.x;
    // Head-on compresses the authored +X smear; grazing stretches it.
    local.x *= mix(1.22, 0.62, grazing);
    if (grazing > 0.55) local.y *= mix(1.0, 1.35, grazing);
    vec2 uv = local / worldSize + 0.5;
    if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) continue;

    float age = clamp(uDamageFieldTime - birth, 0.0, 1.0);
    float fresh = (1.0 - age * 0.32) * restFade;
    gDecalFresh = min(gDecalFresh, fresh);

    // Opacity jitter ±25%.
    float opacity = mix(0.75, 1.25, decalHash11(seed * 9.2 + 2.4));
    opacity = min(opacity, 1.0);

    float row = decalRowFromEnergy(energy, grazing);
    float col = mod(variant, 4.0);
    vec2 atlasUv = decalAtlasUv(col, row, uv);
    vec4 samp = texture2D(uDecalAtlas, atlasUv);
    decalAccumulateSample(samp, fresh, opacity, uv);

    // Heavy hits composite a second tile at independent rotation (max-blend).
    if (energy > 0.62 && grazing < 0.28) {
      float seed2 = decalHash11(seed * 5.7 + 4.1);
      float variant2 = floor(decalHash11(seed * 11.3 + 0.3) * 4.0);
      float rot2 = rot + (seed2 - 0.5) * 2.4 + 0.9;
      float cs2 = cos(-rot2);
      float sn2 = sin(-rot2);
      vec2 local2 = vec2(cs2 * planar.x - sn2 * planar.y, sn2 * planar.x + cs2 * planar.y);
      if (decalHash11(seed * 2.2) > 0.5) local2.x = -local2.x;
      local2.x *= 1.18;
      float scale2 = worldSize * mix(0.78, 1.05, seed2);
      vec2 uv2 = local2 / scale2 + 0.5;
      if (uv2.x >= 0.0 && uv2.x <= 1.0 && uv2.y >= 0.0 && uv2.y <= 1.0) {
        float row2 = energy > 0.82 ? 0.0 : 1.0;
        vec2 atlasUv2 = decalAtlasUv(variant2, row2, uv2);
        vec4 samp2 = texture2D(uDecalAtlas, atlasUv2);
        decalAccumulateSample(samp2, fresh, opacity * 0.85, uv2);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Brick masonry decals — grid-governed (stair cracks, per-brick spall, skids)
// Requires brickSurfaceFields globals already filled (gBrickMortar etc.) OR
// recomputes the analytic grid from restPos using the same constants.
// textureScale is passed so UV matches brickSurfaceFields.
// ---------------------------------------------------------------------------
void sampleBrickDamageDecals(
  vec3 restPos,
  vec3 restNormal,
  float textureScale,
  float mortarMask,
  float brickChip,
  float edgeX,
  float edgeY,
  float brickRow,
  float brickCol,
  float brickX,
  float brickY
) {
  gDecalSpall = 0.0;
  gDecalCrack = 0.0;
  gDecalCrackCore = 0.0;
  gDecalDepth = 0.0;
  gDecalAo = 0.0;
  gDecalRim = 0.0;
  gDecalFresh = 1.0;
  gDecalRestFade = decalEffectiveRestFade();
  gBrickStairCrack = 0.0;
  gBrickStairHair = 0.0;
  gBrickCornerChip = 0.0;
  gBrickSkid = 0.0;
  gStoneJointCrack = 0.0;
  gStoneJointHair = 0.0;
  gStoneCornerChip = 0.0;
  gStoneSkid = 0.0;
  gWoodFurrow = 0.0;
  if (uDecalAtlasReady < 0.5) return;
  if (decalEffectiveRestFade() < 0.02) return;

  int n = int(uDecalStampCount + 0.5);
  if (n <= 0) return;
  vec3 faceN = decalFaceNormal(restNormal);
  float restFade = decalEffectiveRestFade();
  float brickFaceClip = 1.0 - clamp(mortarMask, 0.0, 1.0);
  float sc = max(textureScale, 0.05);

  // Early-out when far from all stamps and no field damage.
  // Reach uses packed stamp radius so a 6 m wall does not scan 48 stamps.
  if (gDamage < 0.001) {
    bool nearStamp = false;
    for (int j = 0; j < DAMAGE_DECAL_STAMP_CAP; j++) {
      if (j >= n) break;
      float e = clamp(uDecalStampB[j].x, 0.0, 1.0);
      float stampRj = max(uDecalStampR[j], mix(0.32, 1.15, e));
      float reach = stampRj * 1.55;
      vec3 dlt = restPos - uDecalStampA[j].xyz;
      vec2 pl = decalFaceCoords(dlt, restNormal);
      if (dot(pl, pl) < reach * reach && abs(dot(dlt, faceN)) < 0.28) {
        nearStamp = true;
        break;
      }
    }
    if (!nearStamp) return;
  }

  // F3 precompute: corner proximity in brick-normalized edge units.
  float cornerProx = (1.0 - smoothstep(0.0, 0.11, edgeX))
    * (1.0 - smoothstep(0.0, 0.11, edgeY));
  float nearImpactMax = 0.0;

  for (int i = 0; i < DAMAGE_DECAL_STAMP_CAP; i++) {
    if (i >= n) break;
    vec4 a = uDecalStampA[i];
    vec4 b = uDecalStampB[i];
    vec3 stampPos = a.xyz;
    float tangentAngle = a.w;
    float energy = clamp(b.x, 0.0, 1.0);
    float seed = b.y;
    float birth = b.z;
    float variant = floor(b.w + 1e-4);
    float grazing = clamp(fract(b.w) / 0.99, 0.0, 1.0);
    float stampR = max(uDecalStampR[i], mix(0.32, 1.15, energy));

    vec3 delta = restPos - stampPos;
    // F1 must still run on mortar rims where interpolated normals tilt;
    // F2 keeps the tighter face gate below.
    if (abs(dot(delta, faceN)) > 0.32) continue;

    vec2 planar = decalFaceCoords(delta, restNormal);
    float dist = length(planar);
    float stairReach = stampR * 1.45;
    float spallCut = stampR * 1.15;
    if (dist > stairReach) continue;

    float age = clamp(uDamageFieldTime - birth, 0.0, 1.0);
    float fresh = (1.0 - age * 0.32) * restFade;
    gDecalFresh = min(gDecalFresh, fresh);
    float falloff = 1.0 - smoothstep(0.0, stairReach, dist);
    vec2 tanDir = vec2(cos(tangentAngle), sin(tangentAngle));

    // Brick-center distance (acceptance is per brick, not per fragment).
    vec2 toCenter = vec2((0.5 - brickX) * 0.98 * sc, (0.5 - brickY) * 0.46 * sc);
    float brickDist = length(planar + toCenter);
    float spallFall = 1.0 - smoothstep(stampR * 0.12, spallCut, brickDist);
    nearImpactMax = max(nearImpactMax, spallFall * energy);

    // ----- F1: stair-step mortar cracks (analytic, no atlas) -----
    // Root cause of the invisible term: a separately reconstructed jointBand
    // (edge-unit smoothstep) did not match the albedo mortar, so the mix
    // factor was ~0 on the pixels that read as grout. Drive the term from
    // mortarMask — the same field that paints the visible joint.
    float mortarJ = clamp(mortarMask, 0.0, 1.0);
    float isHoriz = step(edgeY, edgeX);
    float brickEdgeMin = min(edgeX, edgeY);
    float edgeFw = max(fwidth(brickEdgeMin), 1.5e-4);
    // Hairline inside the joint, screen-space min-width ≥ 1.5 px.
    float hairHalf = max(edgeFw * 1.15, 0.010);
    float hair = 1.0 - smoothstep(hairHalf * 0.18, hairHalf, brickEdgeMin);
    float segRow = brickRow + (1.0 - isHoriz) * step(0.5, brickY);
    float segCol = brickCol + isHoriz * step(0.5, brickX);
    float segId = segRow * 97.0 + segCol * 13.0 + isHoriz * 3.7 + seed * 41.0;
    float keep = decalHash11(segId);
    float align = abs(dot(tanDir, mix(vec2(0.0, 1.0), vec2(1.0, 0.0), isHoriz)));
    float pKeep = falloff * mix(0.24, 0.56, energy) * mix(0.32, 1.22, align);
    float branchKeep = decalHash11(segId * 1.73 + 9.1);
    float pBranch = falloff * mix(0.12, 0.40, energy) * (1.0 - align * 0.25);
    float gated = max(
      step(1.0 - clamp(pKeep, 0.0, 1.0), keep),
      step(1.0 - clamp(pBranch, 0.0, 1.0), branchKeep) * 0.85
    );
    float gap = step(0.20, decalHash11(segId * 2.91 + 0.4));
    float path = gated * gap * falloff * fresh;
    gBrickStairCrack = max(gBrickStairCrack, path * mortarJ);
    gBrickStairHair = max(gBrickStairHair, path * hair * mix(0.75, 1.0, mortarJ));

    // ----- F2: per-brick face spall (atlas + mortar clip) -----
    // Probability is evaluated at the brick CENTER and is 0 by 1.15× stamp R.
    bool faceOk = abs(dot(normalize(restNormal), faceN)) >= 0.55
      && abs(dot(delta, faceN)) <= 0.16;
    if (faceOk && brickDist <= spallCut && spallFall > 0.001
        && brickFaceClip > 0.05 && grazing < 0.55) {
      float brickId = brickRow * 131.0 + brickCol * 17.0 + seed * 29.0;
      float roll = decalHash11(brickId);
      float nearRing = 1.0 - smoothstep(0.0, stampR * 0.40, brickDist);
      float midRing = smoothstep(stampR * 0.28, stampR * 0.72, brickDist)
        * (1.0 - smoothstep(stampR * 0.88, spallCut, brickDist));
      float pSpall = nearRing * mix(0.70, 0.97, energy)
        + midRing * mix(0.16, 0.50, energy);
      if (roll < pSpall) {
        float faceJit = decalHash11(brickId + 4.2);
        vec2 faceUv = vec2(brickX, brickY);
        faceUv = faceUv * mix(0.88, 1.12, faceJit) + (faceJit - 0.5) * 0.08;
        faceUv = clamp(faceUv, 0.0, 1.0);
        float atlasRow = (energy > 0.55 && nearRing > 0.45) ? 1.0 : 0.0;
        float col = mod(variant + floor(decalHash11(brickId * 0.7) * 2.0), 4.0);
        vec2 atlasUv = decalAtlasUv(col, atlasRow, faceUv);
        vec4 samp = texture2D(uDecalAtlasBrick, atlasUv);
        float faceAmt = brickFaceClip * fresh * mix(0.85, 1.0, spallFall);
        samp.g *= mix(0.50, 1.0, nearRing);
        samp.g = min(1.0, samp.g * 1.20);
        // B3: kill R-channel hairline contrast at the G-mask border (dotted ring).
        float interior = smoothstep(0.14, 0.46, samp.g);
        samp.r = mix(0.50, samp.r, mix(0.20, 1.0, interior));
        decalAccumulateSample(samp, faceAmt, 1.0, faceUv);
      }
    }

    // ----- F4: grazing skid row (crosses joints legitimately) -----
    if (grazing > 0.28 && falloff > 0.05) {
      float scaleJit = mix(0.85, 1.35, decalHash11(seed * 17.13 + 0.7));
      // Elongated along tangent: larger world size on the travel axis.
      float worldSize = mix(0.7, 2.1, energy) * scaleJit;
      float rot = tangentAngle + (seed - 0.5) * 0.2;
      float cs = cos(-rot);
      float sn = sin(-rot);
      vec2 local = vec2(cs * planar.x - sn * planar.y, sn * planar.x + cs * planar.y);
      // Stretch along +X (authored skid direction).
      local.x *= 0.55;
      local.y *= 1.35;
      vec2 uv = local / worldSize + 0.5;
      if (uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0) {
        float col = mod(variant, 4.0);
        vec2 atlasUv = decalAtlasUv(col, 2.0, uv); // row 2 = skid
        vec4 samp = texture2D(uDecalAtlasBrick, atlasUv);
        float skidAmt = falloff * fresh * mix(0.55, 1.0, grazing);
        // Skid lightens/abrades (tracked via gBrickSkid); still feed G for rim/depth.
        float prevSpall = gDecalSpall;
        decalAccumulateSample(samp, skidAmt, 0.9, uv);
        // Attribute the added spall mass to skid (for lighten vs darken path).
        gBrickSkid = max(gBrickSkid, max(0.0, gDecalSpall - prevSpall) + samp.g * skidAmt * 0.5);
      }
    }

    // Blast-ish heavy hits: optional mortar-dust patch (row 3) near center.
    if (energy > 0.72 && grazing < 0.22 && dist < spallCut * 0.55) {
      float dustSize = mix(0.35, 0.75, energy);
      vec2 duv = planar / dustSize + 0.5;
      if (duv.x >= 0.0 && duv.x <= 1.0 && duv.y >= 0.0 && duv.y <= 1.0) {
        float col = mod(variant + 1.0, 4.0);
        vec2 atlasUv = decalAtlasUv(col, 3.0, duv);
        vec4 samp = texture2D(uDecalAtlasBrick, atlasUv);
        // Dust sits mainly on mortar + lightly on faces.
        float dustAmt = mix(mortarMask, 0.35, 0.4) * fresh * spallFall;
        decalAccumulateSample(samp, dustAmt * 0.65, 0.8, duv);
      }
    }
  }

  // ----- F3: corner chips biased to grid intersections near impacts -----
  // Kill uniform face freckles near impacts (they read as noise).
  float chipKill = smoothstep(0.08, 0.45, nearImpactMax);
  float cornerBoost = cornerProx * nearImpactMax * brickFaceClip;
  gBrickCornerChip = max(0.0, cornerBoost * 0.95 - brickChip * chipKill * 0.5);
  // Suppress residual freckle contribution for the material to read.
  gBrickCornerChip = max(gBrickCornerChip, cornerBoost * 0.7);
}

// ---------------------------------------------------------------------------
// Stone ashlar decals — grid-governed (joint cracks, per-block spall, skids)
// Occupancy is evaluated at the BLOCK CENTER (never a per-fragment disc).
// ---------------------------------------------------------------------------
void sampleStoneDamageDecals(
  vec3 restPos,
  vec3 restNormal,
  float textureScale,
  float jointMask,
  float edgeX,
  float edgeY,
  float blockRow,
  float blockCol,
  float blockX,
  float blockY,
  float blockW,
  float courseH
) {
  gDecalSpall = 0.0;
  gDecalCrack = 0.0;
  gDecalCrackCore = 0.0;
  gDecalDepth = 0.0;
  gDecalAo = 0.0;
  gDecalRim = 0.0;
  gDecalFresh = 1.0;
  gDecalRestFade = decalEffectiveRestFade();
  gBrickStairCrack = 0.0;
  gBrickStairHair = 0.0;
  gBrickCornerChip = 0.0;
  gBrickSkid = 0.0;
  gStoneJointCrack = 0.0;
  gStoneJointHair = 0.0;
  gStoneCornerChip = 0.0;
  gStoneSkid = 0.0;
  gWoodFurrow = 0.0;
  if (uDecalAtlasReady < 0.5) return;
  if (decalEffectiveRestFade() < 0.02) return;

  int n = int(uDecalStampCount + 0.5);
  if (n <= 0) return;
  vec3 faceN = decalFaceNormal(restNormal);
  float restFade = decalEffectiveRestFade();
  float faceClip = 1.0 - clamp(jointMask, 0.0, 1.0);
  // Harder than albedo mortar: occupancy that overlaps a groove is clipped
  // at the joint line (same rule as brick spall). Grazing skids still cross.
  float jointClip = 1.0 - smoothstep(0.02, 0.14, clamp(jointMask, 0.0, 1.0));
  float sc = max(textureScale, 0.05);
  float worldW = max(blockW, 0.22) * sc;
  float worldH = max(courseH, 0.22) * sc;

  if (gDamage < 0.001) {
    bool nearStamp = false;
    for (int j = 0; j < DAMAGE_DECAL_STAMP_CAP; j++) {
      if (j >= n) break;
      float e = clamp(uDecalStampB[j].x, 0.0, 1.0);
      float stampRj = max(uDecalStampR[j], mix(0.32, 1.15, e));
      float reach = stampRj * 1.55;
      vec3 dlt = restPos - uDecalStampA[j].xyz;
      vec2 pl = decalFaceCoords(dlt, restNormal);
      if (dot(pl, pl) < reach * reach && abs(dot(dlt, faceN)) < 0.28) {
        nearStamp = true;
        break;
      }
    }
    if (!nearStamp) return;
  }

  float cornerProx = (1.0 - smoothstep(0.0, 0.13, edgeX))
    * (1.0 - smoothstep(0.0, 0.13, edgeY));
  float edgeProx = min(
    1.0 - smoothstep(0.0, 0.14, edgeX),
    1.0 - smoothstep(0.0, 0.14, edgeY)
  );
  float nearImpactMax = 0.0;
  float fp = max(max(fwidth(restPos.x), fwidth(restPos.y)), fwidth(restPos.z));

  for (int i = 0; i < DAMAGE_DECAL_STAMP_CAP; i++) {
    if (i >= n) break;
    vec4 a = uDecalStampA[i];
    vec4 b = uDecalStampB[i];
    vec3 stampPos = a.xyz;
    float tangentAngle = a.w;
    float energy = clamp(b.x, 0.0, 1.0);
    float seed = b.y;
    float birth = b.z;
    float variant = floor(b.w + 1e-4);
    float grazing = clamp(fract(b.w) / 0.99, 0.0, 1.0);
    float stampR = max(uDecalStampR[i], mix(0.32, 1.15, energy));

    vec3 delta = restPos - stampPos;
    if (abs(dot(delta, faceN)) > 0.32) continue;

    vec2 planar = decalFaceCoords(delta, restNormal);
    float dist = length(planar);
    float stairReach = stampR * 1.45;
    float spallCut = stampR * 1.15;
    if (dist > stairReach) continue;

    float age = clamp(uDamageFieldTime - birth, 0.0, 1.0);
    float fresh = (1.0 - age * 0.32) * restFade;
    gDecalFresh = min(gDecalFresh, fresh);
    float falloff = 1.0 - smoothstep(0.0, stairReach, dist);
    vec2 tanDir = vec2(cos(tangentAngle), sin(tangentAngle));

    // Block-center distance — acceptance is per block, not per fragment.
    vec2 toCenter = vec2((0.5 - blockX) * worldW, (0.5 - blockY) * worldH);
    float blockDist = length(planar + toCenter);
    float spallFall = 1.0 - smoothstep(stampR * 0.12, spallCut, blockDist);
    nearImpactMax = max(nearImpactMax, spallFall * energy);

    // Joint-following cracks (analytic). Drive from jointMask so the mix
    // lands on the same pixels the albedo paints as mortar.
    float jointJ = clamp(jointMask, 0.0, 1.0);
    float isHoriz = step(edgeY, edgeX);
    float edgeMin = min(edgeX, edgeY);
    float hairHalf = max(fp / max(min(worldW, worldH), 1e-4) * 1.15, 0.010);
    float hair = 1.0 - smoothstep(hairHalf * 0.18, hairHalf, edgeMin);
    float segRow = blockRow + (1.0 - isHoriz) * step(0.5, blockY);
    float segCol = blockCol + isHoriz * step(0.5, blockX);
    float segId = segRow * 97.0 + segCol * 13.0 + isHoriz * 3.7 + seed * 41.0;
    float keep = decalHash11(segId);
    float align = abs(dot(tanDir, mix(vec2(0.0, 1.0), vec2(1.0, 0.0), isHoriz)));
    float pKeep = falloff * mix(0.22, 0.58, energy) * mix(0.30, 1.25, align);
    float branchKeep = decalHash11(segId * 1.73 + 9.1);
    float pBranch = falloff * mix(0.10, 0.38, energy) * (1.0 - align * 0.22);
    float gated = max(
      step(1.0 - clamp(pKeep, 0.0, 1.0), keep),
      step(1.0 - clamp(pBranch, 0.0, 1.0), branchKeep) * 0.85
    );
    float gap = step(0.18, decalHash11(segId * 2.91 + 0.4));
    float path = gated * gap * falloff * fresh;
    gStoneJointCrack = max(gStoneJointCrack, path * jointJ);
    gStoneJointHair = max(gStoneJointHair, path * hair * mix(0.75, 1.0, jointJ));

    // Per-block face spall. Probability at the BLOCK CENTER; 0 by 1.15× stamp R.
    bool faceOk = abs(dot(normalize(restNormal), faceN)) >= 0.55
      && abs(dot(delta, faceN)) <= 0.16;
    if (faceOk && blockDist <= spallCut && spallFall > 0.001
        && jointClip > 0.08 && grazing < 0.55) {
      float blockId = blockRow * 131.0 + blockCol * 17.0 + seed * 29.0;
      float roll = decalHash11(blockId);
      float nearRing = 1.0 - smoothstep(0.0, stampR * 0.40, blockDist);
      float midRing = smoothstep(stampR * 0.28, stampR * 0.72, blockDist)
        * (1.0 - smoothstep(stampR * 0.88, spallCut, blockDist));
      float cornerBias = mix(0.55, 1.35, cornerProx);
      float edgeBias = mix(0.70, 1.22, edgeProx);
      float pSpall = nearRing * mix(0.88, 0.99, energy) * cornerBias
        + midRing * mix(0.20, 0.55, energy) * edgeBias;
      if (nearRing > 0.50) pSpall = max(pSpall, 0.97);
      if (roll < pSpall) {
        float faceJit = decalHash11(blockId + 4.2);
        // Tile coverage scales with energy: stretching the tile across the whole
        // block face made a LIGHT tap paint a block-sized pancake (severity
        // ordering inverted). Low energy -> the tile occupies ~38% of the face.
        float faceSpan = mix(0.38, 1.0, energy);
        vec2 faceUv = (vec2(blockX, blockY) - 0.5) / max(faceSpan, 1e-3) + 0.5;
        faceUv = (faceUv - 0.5) * mix(0.88, 1.12, faceJit) + 0.5 + (faceJit - 0.5) * 0.08;
        if (faceUv.x >= 0.0 && faceUv.x <= 1.0 && faceUv.y >= 0.0 && faceUv.y <= 1.0) {
          float atlasRow = decalRowFromEnergy(energy, 0.0);
          float col = mod(variant + floor(decalHash11(blockId * 0.7) * 2.0), 4.0);
          vec2 atlasUv = decalAtlasUv(col, atlasRow, faceUv);
          vec4 samp = texture2D(uDecalAtlasStone, atlasUv);
          float faceAmt = jointClip * fresh * mix(0.85, 1.0, spallFall);
          // Occupancy scale only — never gate the crack SDF by a G threshold
          // (that re-introduces dashed isolines along the chip rim).
          samp.g = min(1.0, samp.g * mix(0.92, 1.10, nearRing) * jointClip);
          samp.b = max(samp.b, samp.g * 0.38);
          decalAccumulateSample(samp, faceAmt, 1.0, faceUv);
        }
      }

      // World-space stamp at the impact so the hit is visible on this block
      // (face-UV pocks sit at a baked offset and can miss the probe crop).
      // Light taps get a ~30cm stamp, not the old 0.72m floor (pancake bug).
      float hitSize = mix(0.30, 1.10, energy);
      vec2 hitLocal = planar / max(hitSize, 0.08);
      vec2 hitUv = hitLocal * mix(0.42, 0.78, energy) + 0.5;
      if (hitUv.x >= 0.0 && hitUv.x <= 1.0 && hitUv.y >= 0.0 && hitUv.y <= 1.0
          && dist < hitSize * 0.72) {
        float hitRow = decalRowFromEnergy(energy, 0.0);
        float hitCol = mod(variant, 4.0);
        vec4 hitSamp = texture2D(uDecalAtlasStone, decalAtlasUv(hitCol, hitRow, hitUv));
        hitSamp.g *= jointClip;
        hitSamp.b = max(hitSamp.b, hitSamp.g * 0.42);
        decalAccumulateSample(hitSamp, jointClip * fresh * mix(0.90, 1.0, nearRing), 1.0, hitUv);
      }

      // Corner chips used to be a flat albedo mix (white wedges on mortar).
      // Sample the light-row pock field, remapped so the tile center (where
      // pocks live) sits at the block corner — grain + fwidth rim + depth.
      if (cornerProx > 0.22 && nearImpactMax > 0.06) {
        vec2 fromCorner = vec2(
          blockX < 0.5 ? blockX / 0.24 : (1.0 - blockX) / 0.24,
          blockY < 0.5 ? blockY / 0.24 : (1.0 - blockY) / 0.24
        );
        vec2 cUv = clamp(fromCorner * 0.34 + 0.33, 0.0, 1.0);
        float colC = mod(variant, 4.0);
        vec4 cSamp = texture2D(uDecalAtlasStone, decalAtlasUv(colC, 2.0, cUv));
        float cAmt = jointClip * fresh * mix(0.70, 1.0, cornerProx);
        cSamp.g *= jointClip;
        cSamp.b = max(cSamp.b, cSamp.g * 0.42);
        decalAccumulateSample(cSamp, cAmt, 1.0, cUv);
      }
    }

    // Grazing skid — crosses joints legitimately, chatter along tangent.
    if (grazing > 0.28 && falloff > 0.05) {
      vec2 bitan = vec2(-tanDir.y, tanDir.x);
      float along = dot(planar, tanDir);
      float across = dot(planar, bitan);
      float gx = 1.70;
      float gy = 0.42;
      vec2 uv = vec2(along / gx + 0.5, across / gy + 0.5);
      if (abs(along) <= gx * 0.52 && abs(across) <= gy * 0.52
          && uv.x >= 0.0 && uv.x <= 1.0 && uv.y >= 0.0 && uv.y <= 1.0) {
        float col = mod(variant, 4.0);
        vec2 atlasUv = decalAtlasUv(col, 3.0, clamp(uv, 0.0, 1.0));
        vec4 samp = texture2D(uDecalAtlasStone, atlasUv);
        samp.g = min(1.0, samp.g * 1.30);
        samp.b = max(samp.b, samp.g * 0.42);
        float skidAmt = falloff * fresh * mix(0.80, 1.0, grazing);
        float prevSpall = gDecalSpall;
        decalAccumulateSample(samp, skidAmt, 1.0, uv);
        gStoneSkid = max(gStoneSkid, max(0.0, gDecalSpall - prevSpall) + samp.g * skidAmt * 0.60);
      }
    }
  }

  float chipKill = smoothstep(0.08, 0.45, nearImpactMax);
  float cornerBoost = cornerProx * nearImpactMax * jointClip;
  gStoneCornerChip = max(0.0, cornerBoost * 0.95 - chipKill * 0.15);
  gStoneCornerChip = max(gStoneCornerChip, cornerBoost * 0.7);
  // Fold the analytic corner term into the atlas spall so grain/rim/depth
  // run on every chip. Do not paint a second flat albedo mix.
  gDecalSpall = max(gDecalSpall, gStoneCornerChip * jointClip);
  gDecalDepth = max(gDecalDepth, gStoneCornerChip * 0.48 * jointClip);
}

// ---------------------------------------------------------------------------
// Wood plank decals — anisotropic along rest-space +X grain.
// Cross-grain grazing still deflects toward grain (shader mix, not a 2nd row).
// ---------------------------------------------------------------------------
void sampleWoodDamageDecals(vec3 restPos, vec3 restNormal) {
  gDecalSpall = 0.0;
  gDecalCrack = 0.0;
  gDecalCrackCore = 0.0;
  gDecalDepth = 0.0;
  gDecalAo = 0.0;
  gDecalRim = 0.0;
  gDecalFresh = 1.0;
  gDecalRestFade = decalEffectiveRestFade();
  gBrickStairCrack = 0.0;
  gBrickStairHair = 0.0;
  gBrickCornerChip = 0.0;
  gBrickSkid = 0.0;
  gStoneJointCrack = 0.0;
  gStoneJointHair = 0.0;
  gStoneCornerChip = 0.0;
  gStoneSkid = 0.0;
  gWoodFurrow = 0.0;
  if (uDecalAtlasReady < 0.5) return;
  if (decalEffectiveRestFade() < 0.02) return;

  int n = int(uDecalStampCount + 0.5);
  if (n <= 0) return;
  vec3 faceN = decalFaceNormal(restNormal);

  if (gDamage < 0.001) {
    bool nearStamp = false;
    for (int j = 0; j < DAMAGE_DECAL_STAMP_CAP; j++) {
      if (j >= n) break;
      float e = clamp(uDecalStampB[j].x, 0.0, 1.0);
      float gzg = clamp(fract(uDecalStampB[j].w) / 0.99, 0.0, 1.0);
      float sizeE = max(e, gzg > 0.28 ? 0.42 : e);
      float reach = mix(0.78, 1.75, sizeE) * 1.15;
      vec3 dlt = restPos - uDecalStampA[j].xyz;
      vec2 pl = decalFaceCoords(dlt, restNormal);
      if (dot(pl, pl) < reach * reach && abs(dot(dlt, faceN)) < 0.14
          && abs(dot(normalize(restNormal), faceN)) >= 0.62) {
        nearStamp = true;
        break;
      }
    }
    if (!nearStamp) return;
  }

  float restFade = decalEffectiveRestFade();
  vec3 an = abs(restNormal);
  // In-plane grain: rest +X on Z/Y faces; on X-facing end-grain use +Y.
  vec2 grainFace = (an.x >= an.y && an.x >= an.z) ? vec2(0.0, 1.0) : vec2(1.0, 0.0);
  float grainAng = atan(grainFace.y, grainFace.x);

  for (int i = 0; i < DAMAGE_DECAL_STAMP_CAP; i++) {
    if (i >= n) break;
    vec4 a = uDecalStampA[i];
    vec4 b = uDecalStampB[i];
    vec3 stampPos = a.xyz;
    float tangentAngle = a.w;
    float energy = clamp(b.x, 0.0, 1.0);
    float seed = b.y;
    float birth = b.z;
    float variant = floor(b.w + 1e-4);
    float grazing = clamp(fract(b.w) / 0.99, 0.0, 1.0);

    vec3 delta = restPos - stampPos;
    if (abs(dot(delta, faceN)) > 0.14) continue;
    if (abs(dot(normalize(restNormal), faceN)) < 0.62) continue;

    float scaleJit = mix(0.85, 1.20, decalHash11(seed * 17.13 + 0.7));
    // Floor size so sub-carve scuffs/grazes still cover the authored furrow.
    float sizeE = max(energy, grazing > 0.28 ? 0.42 : 0.22);
    float worldSize = mix(0.78, 1.75, sizeE) * scaleJit;
    vec2 planar = decalFaceCoords(delta, restNormal);
    if (grazing <= 0.28) {
      vec2 grainReach = planar;
      grainReach.x *= 0.55;
      grainReach.y *= 1.05;
      if (dot(grainReach, grainReach) > worldSize * worldSize * 0.72) continue;
    }

    vec2 tanDir = vec2(cos(tangentAngle), sin(tangentAngle));
    float tanAlign = abs(dot(tanDir, grainFace));
    // Cross-grain still deflects toward grain (mix 0.12..0.62 with alignment).
    float rot = mix(grainAng, tangentAngle, mix(0.12, 0.62, tanAlign));
    rot += (seed - 0.5) * 0.10;
    bool mirror = decalHash11(seed * 3.1 + 1.7) > 0.5;
    float cs = cos(-rot);
    float sn = sin(-rot);
    vec2 local = vec2(cs * planar.x - sn * planar.y, sn * planar.x + cs * planar.y);
    if (mirror) local.x = -local.x;
    vec2 uv;
    if (grazing > 0.28) {
      // Zoom into the authored gouge (center ~18% of the tile in Y) so the
      // furrow is tens of cm wide, not a 3 cm hairline.
      float gx = 1.70;
      float gy = 0.50;
      uv = vec2(planar.x / gx * 0.72 + 0.5, planar.y / gy * 0.32 + 0.5);
      if (abs(planar.x) > gx * 0.52 || abs(planar.y) > gy * 0.52) continue;
    } else {
      local.x *= mix(0.58, 0.72, energy);
      local.y *= mix(0.92, 1.00, grazing);
      uv = local / worldSize + 0.5;
      if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) continue;
    }
    uv = clamp(uv, 0.0, 1.0);

    float age = clamp(uDamageFieldTime - birth, 0.0, 1.0);
    float fresh = (1.0 - age * 0.32) * restFade;
    gDecalFresh = min(gDecalFresh, fresh);
    float opacity = mix(0.90, 1.0, decalHash11(seed * 9.2 + 2.4));

    float row = decalRowFromEnergy(energy, grazing);
    float col = mod(variant, 4.0);
    vec2 atlasUv = decalAtlasUv(col, row, uv);
    vec4 samp = texture2D(uDecalAtlasWood, atlasUv);
    if (grazing > 0.28) {
      samp.g = min(1.0, samp.g * 1.35);
      samp.b = max(samp.b, samp.g * 0.40);
    }
    float prevSpall = gDecalSpall;
    decalAccumulateSample(samp, fresh, opacity, uv);
    if (grazing > 0.28) {
      gWoodFurrow = max(gWoodFurrow, max(0.0, gDecalSpall - prevSpall) + samp.g * fresh * opacity * 0.70);
    }

    if (energy > 0.62 && grazing < 0.28) {
      float seed2 = decalHash11(seed * 5.7 + 4.1);
      float variant2 = floor(decalHash11(seed * 11.3 + 0.3) * 4.0);
      float rot2 = rot + (seed2 - 0.5) * 0.35;
      float cs2 = cos(-rot2);
      float sn2 = sin(-rot2);
      vec2 local2 = vec2(cs2 * planar.x - sn2 * planar.y, sn2 * planar.x + cs2 * planar.y);
      if (decalHash11(seed * 2.2) > 0.5) local2.x = -local2.x;
      local2.x *= 0.42;
      float scale2 = worldSize * mix(0.78, 1.05, seed2);
      vec2 uv2 = local2 / scale2 + 0.5;
      if (uv2.x >= 0.0 && uv2.x <= 1.0 && uv2.y >= 0.0 && uv2.y <= 1.0) {
        float row2 = energy > 0.82 ? 0.0 : 1.0;
        vec2 atlasUv2 = decalAtlasUv(variant2, row2, uv2);
        vec4 samp2 = texture2D(uDecalAtlasWood, atlasUv2);
        decalAccumulateSample(samp2, fresh, opacity * 0.85, uv2);
      }
    }
  }
}
#endif
#endif
`;

/**
 * Vertex rest-fade: |worldPos − (wallOrigin + restPos)| → 1 at rest, 0 by ~3 cm.
 * Inject after worldpos_vertex so batching/instancing matrices are applied.
 */
export const DAMAGE_DECAL_VERTEX_GLSL = /* glsl */ `
#ifdef USE_DAMAGE_DECAL_ATLAS
uniform vec3 uDecalWallOrigin;
uniform float uBakeDownDecals;
varying float vDecalRestFade;
#endif
`;

export const DAMAGE_DECAL_VERTEX_SAMPLE = /* glsl */ `
#ifdef USE_DAMAGE_DECAL_ATLAS
{
  vec4 decalLocal = vec4(transformed, 1.0);
  #ifdef USE_BATCHING
  decalLocal = batchingMatrix * decalLocal;
  #endif
  #ifdef USE_INSTANCING
  decalLocal = instanceMatrix * decalLocal;
  #endif
  vec3 decalWorld = (modelMatrix * decalLocal).xyz;
  vec3 decalRestWorld = uDecalWallOrigin + surfaceRestPosition;
  float decalDisp = length(decalWorld - decalRestWorld);
  // Soft fade 1.5 cm → 3 cm so barely-settled cells keep marks.
  vDecalRestFade = 1.0 - smoothstep(0.015, 0.03, decalDisp);
  // Bake-down is rest-space atlas capture — never fade stamps while freezing them.
  if (uBakeDownDecals > 0.5) vDecalRestFade = 1.0;
}
#endif
`;
